import type { Theme } from "../types/Theme";
import { themeNumber, themeOptionalColorString } from "../utils";
import { controlBackgroundColor, foregroundColor } from "./Mixins";
import { shouldUseModernTheme } from "./navigationButtonThemeColors";

export function useModernChoiceStyles(theme: Theme): boolean {
    return shouldUseModernTheme(theme);
}

export function resolveChoicePaddingModern(theme: Theme): number[][] {
    return theme.ChoicePaddingModern ?? [[8, 16]];
}

/**
 * Choice surface corner radius — Choice List → Choices Background → Corners
 * ({@link Theme.ChoiceListBackgroundBorderRadius}), clamped to a pill at the choice height. Same
 * formula in both themes. The modern focus outline and inset border follow it automatically, since
 * CSS `outline` and `box-shadow` both track the element's own border-radius.
 */
export function resolveChoiceBorderRadius(theme: Theme): number {
    return Math.min(theme.ChoiceListBackgroundBorderRadius, theme.ChoiceHeight / 2);
}

export function resolveChoiceIconGapModern(theme: Theme): number {
    return theme.ChoiceIconGapModern ?? 12;
}

export function resolveChoiceBorderColorNormal(theme: Theme): string {
    return themeOptionalColorString(theme.ChoiceBorderColorNormal) ?? theme.ControlBorderColor;
}

export function resolveChoiceBorderColorHover(theme: Theme): string {
    return themeOptionalColorString(theme.ChoiceBorderColorHover) ?? resolveChoiceBorderColorNormal(theme);
}

export function resolveChoiceBackgroundHover(theme: Theme): string {
    return themeOptionalColorString(theme.ChoiceBackgroundHover) ?? theme.NeutralLightColor;
}

export function resolveChoiceBackgroundSelectedHover(theme: Theme): string {
    return (
        themeOptionalColorString(theme.ChoiceBackgroundSelectedHover) ??
        resolveChoiceBackgroundHover(theme)
    );
}

export function resolveChoiceFocusOutlineColor(theme: Theme): string {
    return themeOptionalColorString(theme.ChoiceFocusOutlineColor) ?? theme.NeutralDarkColor;
}

/** Modern choice keyboard-focus ring stroke width (px). */
export const CHOICE_FOCUS_OUTLINE_WIDTH_PX = 2;

/** Gap between choice border and focus ring via `outline-offset` (px). */
export const CHOICE_FOCUS_OUTLINE_OFFSET_PX = 2;

/** Primary inset border width for modern selected choices and focused inputs (px). */
export const MODERN_ACTIVE_BORDER_WIDTH_PX = 2;

export function resolveModernChoiceFocusColor(theme: Theme, selected?: boolean): string {
    if (selected) {
        return theme.PrimaryColor;
    }
    return resolveChoiceFocusOutlineColor(theme);
}

export function resolveModernChoiceFocusOutline(theme: Theme, selected?: boolean): string {
    return `${CHOICE_FOCUS_OUTLINE_WIDTH_PX}px solid ${resolveModernChoiceFocusColor(theme, selected)}`;
}

export function resolveChoiceIconUncheckedColor(theme: Theme): string {
    return (
        themeOptionalColorString(theme.ChoiceIconUncheckedColor) ??
        theme.NeutralDarkColor ??
        theme.ForegroundColorDark
    );
}

/** Unselected modern choice label; contrast against the choice surface as actually rendered (blended over the page at `ControlAlpha`), not the raw swatch. */
export function resolveModernChoiceLabelColor(theme: Theme): string {
    return foregroundColor(theme, controlBackgroundColor(theme));
}

export function resolveChoiceIconSizeModern(theme: Theme): number {
    return theme.ChoiceIconSizeModern ?? 16;
}

export function resolveChoiceRadioIconSizeModern(_theme: Theme): number {
    return 18;
}

export function resolveChoiceLabelFontWeight(theme: Theme): number {
    return theme.ChoiceLabelFontWeight ?? 400;
}

/** Resting choice border width — Choice List → Choices Background → Borders. */
export function resolveChoiceBorderWidth(theme: Theme): number {
    return theme.ChoiceListBackgroundBorderWidth ?? theme.BorderWidth ?? 1;
}

/**
 * Selected / focused / expanded accent width. Keeps the modern 2px emphasis at the 1px default, but
 * collapses to 0 when borders are switched off and follows the theme at 3px — so it matches legacy
 * exactly at both ends of the Borders setting.
 *
 * The width is coerced defensively: it arrives from the untyped theme `settings` bag, where nothing
 * enforces a number, and an unparsed "0" would escape the `=== 0` check and land on
 * `Math.max("0", 2)` → 2 — a focus border on a field whose Borders setting is "none". No observed
 * theme sends strings, so this guards a shape the type allows rather than a reproduced bug; the
 * causes of DEV-98424 are documented on `resolveFieldUnderlineShadow` (the `border` shorthand being
 * applied after `border-bottom`) and `resolveTextInputBackground` (the byte-identical page colour).
 */
export function resolveEmphasisBorderWidth(width: number | string): number {
    const borderWidth = themeNumber(width, 0);
    return borderWidth === 0 ? 0 : Math.max(borderWidth, MODERN_ACTIVE_BORDER_WIDTH_PX);
}

export function resolveModernChoiceInsetBorder(
    theme: Theme,
    borderColor: string,
    width?: number,
): string {
    const borderWidth = width ?? resolveChoiceBorderWidth(theme);
    return `0 0 0 ${borderWidth}px ${borderColor} inset`;
}

/** Primary inset border for selected choices; honours Choices Background → Borders. */
export function resolveModernActiveChoiceInsetBorder(theme: Theme, borderColor: string): string {
    return resolveModernChoiceInsetBorder(
        theme,
        borderColor,
        resolveEmphasisBorderWidth(resolveChoiceBorderWidth(theme)),
    );
}

/**
 * JSS fragment for modern ChoiceGrid/HybridGrid matrix radio/checkbox cells.
 * Grey keyboard focus border (always neutral, even when checked); suppresses native outline.
 */
export function modernGridMatrixCellFocusStyles(theme: Theme): Record<string, unknown> {
    return {
        border: `${CHOICE_FOCUS_OUTLINE_WIDTH_PX}px solid transparent`,
        boxSizing: "border-box",
        outline: "none",
        transition: "border-color 200ms",
        "&:focus": {
            outline: "none",
        },
        "&:focus-visible": {
            borderColor: resolveChoiceFocusOutlineColor(theme),
            outline: "none",
        },
    };
}

